/**
 * Utility functions for building CSS class names, especially for button variants.
 */

type StyleModule = {
    [key: string]: string;
};

/**
 * Builds a className string for color variant buttons.
 * Consolidates the repeated className construction logic across button components.
 *
 * @param styles - CSS module styles object
 * @param baseClass - The base class name key (e.g., "SecondaryButton")
 * @param options - Configuration options
 * @returns Combined className string
 */
export function getColorVariantClass(
    styles: StyleModule,
    baseClass: string,
    options: {
        colorVariant?: string;
        disabled?: boolean;
        className?: string;
    } = {}
): string {
    const { colorVariant = "neutral", disabled = false, className } = options;

    const classes: string[] = [styles[baseClass]];

    if (className) {
        classes.push(className);
    }

    if (disabled) {
        classes.push(styles.Disabled);
    }

    if (colorVariant === "secondary") {
        classes.push(styles.Secondary);
    } else if (colorVariant === "primary") {
        classes.push(styles.Primary);
    } else if (colorVariant === "neutral") {
        classes.push(styles.Neutral);
    }

    return classes.filter(Boolean).join(" ");
}

/**
 * Simple utility to join class names, filtering out falsy values.
 *
 * @param classes - Array of class names or falsy values
 * @returns Combined className string
 */
export function cn(...classes: (string | undefined | null | false)[]): string {
    return classes.filter(Boolean).join(" ");
}
